/*Copyright (c) 2025 por Tecnologías de Información MALMUR y empresas afiliadas. Todos los derechos reservados.
Este software es información confidencial y propietaria de Copyright (c) 2025 por Tecnologías de Información MALMUR
 y sus afiliadas. ("Información Confidencial"). Usted no puede divulgar dicha Información Confidencial, 
 y solo puede usar dicha Información Confidencial de acuerdo con los términos del acuerdo de licencia que celebró con MALMUR.*/

/*Copyright (c) 2025 by Tecnologías de Información MALMUR and affiliated companies. All Rights Reserved.
This software is confidential and proprietary information of Copyright (c) 2025 by Tecnologías de Información 
MALMUR and its affiliates. ("Confidential Information"). You may not disclose such Confidential Information, 
and may only use such Confidential Information in accordance with the terms of the license agreement you entered 
into with MALMUR.*/



/**
=========================================================
* Material Dashboard 2 PRO React TS - v1.0.2
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-2-pro-react-ts
* Copyright 2023 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

import { useState, useEffect, useCallback, useRef } from "react";

// react-router components
import { useLocation, useNavigate } from "react-router-dom";

// @material-ui core components
import AppBar from "@mui/material/AppBar";
import Toolbar from "@mui/material/Toolbar";
import IconButton from "@mui/material/IconButton";
import Menu from "@mui/material/Menu";
import Icon from "@mui/material/Icon";
import Popover from "@mui/material/Popover";
import Box from "@mui/material/Box";

// Material Dashboard 2 PRO React TS components
import MDBox from "dashboard-template/components/MDBox";
import MDBadge from "dashboard-template/components/MDBadge";

// Material Dashboard 2 PRO React TS examples components
import Breadcrumbs from "dashboard-template/examples/Breadcrumbs";
import NotificationItem from "dashboard-template/examples/Items/NotificationItem";
import NotificationWidget from "components/widgets/NotificationWidget";
import { useTranslation } from "react-i18next";
import {ArrAppointmentsActions} from "store/slices/appointment.slice";

// Custom styles for DashboardNavbar
import {
  navbar,
  navbarContainer,
  navbarRow,
  navbarIconButton,
  navbarDesktopMenu,
  navbarMobileMenu,
} from "dashboard-template/examples/Navbars/DashboardNavbar/styles";

// Material Dashboard 2 PRO React context
import {
  useMaterialUIController,
  setTransparentNavbar,
  setMiniSidenav,
} from "context/ThemeContext";
import { authAction, useAppDispatch } from "store/store";
import { selectCurrentUser } from "store/slices/auth.slice";
import useHttp from "hooks/http.hook";
import { CoreAPI_URLs } from "constants/http.const";
import { HeaderEnum, UserInterface } from "medicalsystem-shared";

import BookOnlineRoundedIcon from "@mui/icons-material/BookOnlineRounded";
import EventBusyRoundedIcon from "@mui/icons-material/EventBusyRounded";
import EditCalendarRoundedIcon from "@mui/icons-material/EditCalendarRounded";
import TransferWithinAStationRoundedIcon from "@mui/icons-material/TransferWithinAStationRounded";
import MDButton from "dashboard-template/components/MDButton";
import { RoutesConstants } from "medicalsystem-shared";
import { NotificationInterface } from 'medicalsystem-shared';
import { useSelector } from 'react-redux';
import { selectNotificationCounts } from 'store/slices/notification.slice';
import SmartFloatingBackButton from 'components/generals/SmartBackButton';

/**
 * Props for DashboardNavbar component.
 */
interface Props {
  absolute?: boolean;
  light?: boolean;
  isMini?: boolean;
}

/**
 * Dashboard navigation bar with authentication and notifications.
 * 
 * Features:
 * - Responsive navbar (fixed/sticky/static positioning)
 * - Transparent navbar on scroll when fixed navbar is enabled
 * - Breadcrumb navigation with i18n support
 * - User menu with settings and logout actions
 * - Notification widget with real-time updates (10s refresh interval)
 * - Unread notification badge counter from Redux state
 * - Smart back button with dynamic color based on theme
 * - Sidebar toggle button for expanding/minimizing sidebar
 * - Developer tools: global persistor reset (visible when REACT_APP_DEV_TOOLS=true)
 * 
 * The navbar automatically:
 * - Sets transparency based on scroll position and fixedNavbar setting
 * - Updates notification counts from Redux store
 * - Synchronizes icon colors with theme (light/dark mode)
 * - Handles RTL/LTR layouts appropriately
 * 
 * @param absolute - Position navbar absolutely (default: false)
 * @param light - Use light color scheme for icons (default: false)
 * @param isMini - Use mini navbar layout (default: false)
 * @returns Dashboard navbar component
 */
function DashboardNavbar({
  absolute = false,
  light = false,
  isMini = false,
}: Readonly<Props>): JSX.Element {
  const [navbarType, setNavbarType] = useState<
    "fixed" | "absolute" | "relative" | "static" | "sticky"
  >();
  const [controller, dispatch] = useMaterialUIController();
  const { miniSidenav, transparentNavbar, fixedNavbar, darkMode } = controller;
  const [openMenu, setOpenMenu] = useState<any>(false);
  const [openUserMenu, setOpenUserMenu] = useState<any>(false);
  const [notificationAnchorEl, setNotificationAnchorEl] = useState<HTMLElement | null>(null);
  const { unreadCount } = useSelector(selectNotificationCounts);
  const [forceRefresh, setForceRefresh] = useState<boolean>(false);
  const reduxDispatch = useAppDispatch();
  const currentUser = useSelector(selectCurrentUser);
  const [_isLoading, _error, sendRequest] = useHttp();
  
  const route = useLocation().pathname.split("/").slice(1);
  const { t } = useTranslation();
  
  const REFRESH_INTERVAL = 10000;

  const saveMiniSidenavToProfile = async (value: boolean) => {
    if (!currentUser) return;

    const updatedProfile = {
      ...(currentUser.profile || {}),
      miniSidenav: value,
    };

    const updatedUser = {
      ...currentUser,
      profile: updatedProfile,
    };

    try {
      await sendRequest<UserInterface>(
        CoreAPI_URLs.USERS_GENERAL_OPERATIONS,
        (response) => {
          if (response) {
            reduxDispatch(authAction.storeUser(response));
          }
        },
        'PUT',
        JSON.stringify(updatedUser),
        [{ header: HeaderEnum.USER_ID, value: currentUser.id }]
      );
    } catch (error) {
      // Error handling
    }
  };

  useEffect(() => {
    // Setting the navbar type
    if (fixedNavbar) {
      setNavbarType("sticky");
    } else {
      setNavbarType("static");
    }

    // A function that sets the transparent state of the navbar.
    function handleTransparentNavbar() {
      setTransparentNavbar(dispatch, (fixedNavbar && window.scrollY === 0) || !fixedNavbar);
    }

    /** 
     The event listener that's calling the handleTransparentNavbar function when 
     scrolling the window.
    */
    window.addEventListener("scroll", handleTransparentNavbar);

    // Call the handleTransparentNavbar function to set the state with the initial value.
    handleTransparentNavbar();

    // Remove event listener on cleanup
    return () => window.removeEventListener("scroll", handleTransparentNavbar);
  }, [dispatch, fixedNavbar]);

  const handleMiniSidenav = async () => {
    const newMiniSidenav = !miniSidenav;
    setMiniSidenav(dispatch, newMiniSidenav);
    await saveMiniSidenavToProfile(newMiniSidenav);
  };
  const handleOpenMenu = (event: any) => setOpenMenu(event.currentTarget);
  const handleCloseMenu = () => setOpenMenu(false);
  const handleOpenUserMenu = (event: any) => setOpenUserMenu(event.currentTarget);
  const handleCloseUserMenu = () => setOpenUserMenu(false);
  
  const handleOpenNotifications = useCallback((event: React.MouseEvent<HTMLElement>) => {
    setNotificationAnchorEl(event.currentTarget);
    setForceRefresh(prev => !prev);
  }, []);
  
  const handleCloseNotifications = useCallback(() => {
    setNotificationAnchorEl(null);
  }, []);

  const handleNotificationClick = useCallback((notification: NotificationInterface) => {
    setNotificationAnchorEl(null);
  }, []);

  const validateSnapShot = process.env.REACT_APP_DEV_TOOLS === "true";
  const translatedRoute = route.map((r) => t(`dashboardNavbar.${r}`));

  // Render the notifications menu
  const renderMenu = () => (
    <Menu
      anchorEl={openMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openMenu)}
      onClose={handleCloseMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem
        icon={<BookOnlineRoundedIcon />}
        title={t("dashboardNavbar.closeAppointment")}
      />
      <NotificationItem
        icon={<EventBusyRoundedIcon />}
        title={t("dashboardNavbar.canceledAppointment")}
      />
      <NotificationItem
        icon={<EditCalendarRoundedIcon />}
        title={t("dashboardNavbar.appointmentRequest")}
      />
      <NotificationItem
        icon={<TransferWithinAStationRoundedIcon />}
        title={t("dashboardNavbar.patientArrived")}
      />
    </Menu>
  );

  const renderNotificationWidget = () => {
    if (!Boolean(notificationAnchorEl)) {
      return null;
    }

    return (
      <Popover
        open={Boolean(notificationAnchorEl)}
        anchorEl={notificationAnchorEl}
        onClose={handleCloseNotifications}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        sx={{
          mt: 1,
          '& .MuiPopover-paper': {
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0, 0, 0, 0.12)',
            border: '1px solid rgba(0, 0, 0, 0.08)',
            overflow: 'visible',
            maxHeight: '80vh',
            minWidth: 400,
            maxWidth: 480,
          }
        }}
      >
        <Box sx={{ width: '100%', height: '600px' }}>
          <NotificationWidget
            refreshInterval={REFRESH_INTERVAL}
            maxNotifications={50}
            showStats={true}
            autoRefresh={true}
            forceRefresh={forceRefresh}
            shouldFetchData={true}
            onNotificationClick={handleNotificationClick}
          />
        </Box>
      </Popover>
    );
  };

  const appDispatch = useAppDispatch();
  const navigate = useNavigate();

  const logoutHandle = () => {
    reduxDispatch(ArrAppointmentsActions.deleteList());
    appDispatch(authAction.logout());
    navigate(RoutesConstants.LOGIN);
  };

  const configHandle = () => {
    navigate(RoutesConstants.USER_PROFILE);
  };

  // Render User menu
  const renderUserMenu = () => (
    <Menu
      id="user-menu"
      anchorEl={openUserMenu}
      anchorReference={null}
      anchorOrigin={{
        vertical: "bottom",
        horizontal: "left",
      }}
      open={Boolean(openUserMenu)}
      onClose={handleCloseUserMenu}
      sx={{ mt: 2 }}
    >
      <NotificationItem
        onClick={configHandle}
        icon={<Icon>settings</Icon>}
        title={t("dashboardNavbar.config")}
      />
      <NotificationItem
        onClick={logoutHandle}
        icon={<Icon>logout</Icon>}
        title={t("dashboardNavbar.logout")}
      />
    </Menu>
  );

  // Styles for the navbar icons
  const iconsStyle = ({
    palette: { dark, white, text },
    functions: { rgba },
  }: {
    palette: any;
    functions: any;
  }) => ({
    color: () => {
      let colorValue = light || darkMode ? white.main : dark.main;

      if (transparentNavbar && !light) {
        colorValue = darkMode ? rgba(text.main, 0.6) : text.main;
      }

      return colorValue;
    },
  });

  const deleteGlobalPersistor = () => {
    localStorage.removeItem("persist:root");
  };
  
  return (
    <>
      <Box sx={{ display: 'none' }}>
        <NotificationWidget
          refreshInterval={REFRESH_INTERVAL}
          maxNotifications={50}
          showStats={false}
          autoRefresh={true}
          shouldFetchData={true}
        />
      </Box>
      
      <AppBar
        position={absolute ? "absolute" : navbarType}
        color="inherit"
        sx={(theme) => navbar(theme, { transparentNavbar, absolute, light, darkMode })}
      >
        <Toolbar sx={navbarContainer}>
          <MDBox color="inherit" mb={{ xs: 1, md: 0 }} sx={(theme) => navbarRow(theme, { isMini })}>
            <MDBox display="flex" alignItems="center" gap={0}>
              <SmartFloatingBackButton 
                iconColor={(() => {
                  let colorValue = light || darkMode ? 'white' : 'rgba(0, 0, 0, 0.6)';
                  
                  if (transparentNavbar && !light) {
                    colorValue = darkMode ? 'rgba(255, 255, 255, 0.6)' : 'rgba(0, 0, 0, 0.6)';
                  }
                  
                  return colorValue;
                })()}
                sx={{
                  backgroundColor: 'rgba(0, 0, 0, 0.05)',
                  borderRadius: '50%',
                  width: 32,
                  height: 32,
                  '&:hover': {
                    backgroundColor: 'rgba(0, 0, 0, 0.1)',
                  },
                }}
              />
              <MDBox margin={"10px"}>
                                          <IconButton onClick={handleMiniSidenav} size="small" disableRipple>
              <Icon fontSize="medium" sx={iconsStyle}>
                {miniSidenav ? "menu_open" : "menu"}
              </Icon>
            </IconButton>
              </MDBox>
              <Breadcrumbs
                icon="home"
                title={translatedRoute[translatedRoute.length - 1]}
                route={route}
                light={light}
              />
            </MDBox>
          </MDBox>
          {isMini ? null : (
           <MDBox sx={(theme) => navbarRow(theme, { isMini })}>
              {validateSnapShot && (
                <MDBox pr={1}>
                  <MDButton
                    onClick={() => {
                      deleteGlobalPersistor();
                    }}
                  >
                    {t("dashboardNavbar.deleteGlobalPersistor")}
                  </MDButton>
                </MDBox>
              )}

              {/*<MDBox pr={1}>
                <MDInput label={t("dashboardNavbar.search")} />
              </MDBox>*/}

              <MDBox color={light ? "white" : "inherit"}>
                <IconButton
                  size="small"
                  disableRipple
                  color="inherit"
                  sx={navbarIconButton}
                  onClick={handleOpenUserMenu}
                >
                  <Icon sx={iconsStyle}>account_circle</Icon>
                </IconButton>
                {renderUserMenu()}
                <IconButton
                  size="small"
                  color="inherit"
                  sx={navbarIconButton}
                  onClick={handleOpenNotifications}
                >
                  <MDBadge badgeContent={unreadCount} color="error" size="xs" circular>
                    <Icon sx={iconsStyle}>notifications</Icon>
                  </MDBadge>
                </IconButton>
                {renderNotificationWidget()}
              </MDBox>
            </MDBox>
          )}
        </Toolbar>
      </AppBar>
    </>
  );
}

export default DashboardNavbar;
